import { gql } from "@apollo/client";

export const CaptureFragment = gql`
  fragment CaptureFragment on BucketCapture {
    id
    captureId

    eventType
    timestamp
    dimensions
    measures
  }
`;

// There is no CaptureGet. A capture has no stable single-row address — the read
// path queries a time window in Timestream through datum — so the listing is the
// only way in and there is no capture detail page to link a row to.
//
// Note `id` and not `captureId` as the row identity: captureId IS the fileId, and
// a create/remove pair shares it. The server's id carries the timestamp for
// exactly this reason.
export const list = gql`
  query BucketCaptureList(
    $bucketId: String!
    $input: ListInput
    $filter: BucketCaptureFilterInput
  ) {
    viewer {
      id
      account {
        id
        buckets {
          id
          get(bucketId: $bucketId) {
            id
            captures {
              id
              list(input: $input, filter: $filter) {
                id
                last
                items {
                  ...CaptureFragment
                }
              }
            }
          }
        }
      }
    }
  }
  ${CaptureFragment}
`;

// `truncated` and `interval` are selected deliberately, not for completeness.
// truncated says the answer was partial; interval is the bin width the server
// actually used, which is not always the one asked for — an axis labelled from
// the request rather than the response is labelled wrong.
export const aggregate = gql`
  query BucketCaptureAggregate(
    $bucketId: String!
    $input: BucketCaptureAggregateInput
    $filter: BucketCaptureFilterInput
  ) {
    viewer {
      id
      account {
        id
        buckets {
          id
          get(bucketId: $bucketId) {
            id
            captures {
              id
              aggregate(input: $input, filter: $filter) {
                id
                interval
                intervalMs
                start
                end
                bins
                measures
                truncated
                series {
                  id
                  key
                  eventType
                  dimensions
                  points
                  totals
                  count
                }
              }
            }
          }
        }
      }
    }
  }
`;

// Nothing in this app writes a capture — they arrive from the upload, unzip,
// minimap and remove handlers — so this is only reachable from a manual refresh.
// It is here because the eviction key is not obvious: it is the
// BucketCaptureQueries namespace id, not the listing id, and the listing id
// deliberately varies per page and per filter.
export const update = ({ bucketId }) => (cache) => {
  Object.keys(cache.data.data).forEach((key) => {
    if (key.includes(`${bucketId}/captures/queries`)) {
      cache.data.delete(key);
    }
  });
};
